import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ClearanceApplication, ClearancePurpose, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const CRO_ROLES = ['CRO_OFFICER'];
const ANY_POLICE_ROLES = ['STATION_OFFICER', 'CRO_OFFICER', 'FIREARMS_REGISTRAR'];

/** Police — clearance certificates (M05): counter-filed applications, auto-issue on a clean index, or manual review. */
export function Clearance() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isCroOfficer = roles.some((r) => CRO_ROLES.includes(r));
  const isAnyPolice = roles.some((r) => ANY_POLICE_ROLES.includes(r));
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showApply, setShowApply] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/police/clearance?${params.toString()}`;
  }, [status]);

  const listQ = useApiQuery<Page<ClearanceApplication>>(path);

  return (
    <div>
      <PageHeader
        title="Clearance Certificates"
        subtitle="Employment/GOP/visa/adoption/licence police clearance applications (M05)."
        actions={
          <div className="flex items-center gap-2">
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All states</option>
              <option value="APPLIED">Applied</option>
              <option value="MANUAL_REVIEW">Manual review</option>
              <option value="ISSUED">Issued</option>
              <option value="ISSUED_WITH_DISCLOSURE">Issued with disclosure</option>
              <option value="REFUSED">Refused</option>
            </select>
            {isAnyPolice ? (
              <button className="btn-primary" onClick={() => setShowApply(true)}>
                + Counter application
              </button>
            ) : null}
          </div>
        }
      />

      {!isCroOfficer ? <RoleNotice roles={CRO_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No clearance applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">NIN</th>
                <th className="table-head">Purpose</th>
                <th className="table-head">Result</th>
                <th className="table-head">Cert serial</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{c.nin}</td>
                  <td className="table-cell">{c.purpose}</td>
                  <td className="table-cell text-xs text-slate-500">{c.resultClass ?? '—'}</td>
                  <td className="table-cell font-mono text-xs">{c.certSerial ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <ClearanceDetailModal
          id={selectedId}
          canReview={isCroOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showApply ? (
        <ApplyModal
          onClose={() => setShowApply(false)}
          onDone={() => {
            setShowApply(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ClearanceDetailModal({
  id,
  canReview,
  onClose,
  onChanged,
}: {
  id: string;
  canReview: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useApiQuery<ClearanceApplication>(`/api/v1/police/clearance/${id}`);
  const c = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<'DISCLOSE' | 'REFUSE' | null>(null);
  const [notes, setNotes] = useState('');

  const review = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/police/clearance/${id}/review`, { decision, notes });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to review');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={c ? `Clearance — ${c.nin}` : 'Clearance application'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="State" value={<StatusBadge status={c.state} />} />
            <Detail label="Purpose" value={c.purpose} />
            <Detail label="Result class" value={c.resultClass ?? '—'} />
            <Detail label="Certificate serial" value={c.certSerial ?? '—'} mono />
            <Detail label="Issued" value={c.certIssuedAt ? formatDateTime(c.certIssuedAt) : '—'} />
            <Detail label="Expires" value={c.certExpiresAt ? formatDateTime(c.certExpiresAt) : '—'} />
          </div>

          {c.reviewNotes ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700">
              Review notes ({c.reviewedBy}): {c.reviewNotes}
            </div>
          ) : null}

          {c.state === 'MANUAL_REVIEW' ? (
            canReview ? (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="text-sm font-semibold text-slate-700">CRO manual review — this NIN has record(s) on file</div>
                <div className="flex gap-2">
                  <button className={`btn-danger ${decision === 'DISCLOSE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('DISCLOSE')}>
                    Disclose record
                  </button>
                  <button className={`btn-success ${decision === 'REFUSE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('REFUSE')}>
                    Refuse certificate
                  </button>
                </div>
                {decision ? (
                  <>
                    <Field label="Notes (required)">
                      <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </Field>
                    <button className="btn-primary" disabled={busy || notes.trim().length < 3} onClick={review}>
                      {busy ? 'Submitting…' : `Confirm ${decision === 'DISCLOSE' ? 'disclosure' : 'refusal'}`}
                    </button>
                  </>
                ) : null}
              </div>
            ) : (
              <RoleNotice roles={CRO_ROLES} />
            )
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Filed {formatDateTime(c.createdAt)} · last updated {formatDateTime(c.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function ApplyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [purpose, setPurpose] = useState<ClearancePurpose>('EMPLOYMENT');
  const [nin, setNin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/police/clearance/apply', { purpose, ...(nin.trim() ? { nin: nin.trim() } : {}) });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to apply');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Counter application — police clearance certificate" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Purpose">
          <select className="field-input" value={purpose} onChange={(e) => setPurpose(e.target.value as ClearancePurpose)}>
            <option value="EMPLOYMENT">Employment</option>
            <option value="GOP">Good conduct (GOP)</option>
            <option value="VISA">Visa</option>
            <option value="ADOPTION">Adoption</option>
            <option value="LICENCE">Licence</option>
          </select>
        </Field>
        <Field label="Applicant NIN (leave blank to apply for yourself)">
          <input className="field-input font-mono" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="e.g. 108-4471-2-2-19" />
        </Field>
        <p className="text-xs text-slate-400">
          A clean Criminal Record Index issues the certificate immediately; a hit sends it to CRO manual review.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy} onClick={submit}>
            {busy ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
